import React, { useContext, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthContext } from "./context/AuthContext";

// --- IMPORT COMPONENTS ---
import Sidebar from "./components/Sidebar";


import Login from "./loginPages/Login";
import DoctorRegistration from "./pages/DoctorRegistration";
import DoctoreSchedule from "./pages/DoctoreSchedule";
import AppointmentBooking from "./pages/AppointmentBooking";
import PatientsRegistration from "./pages/PatientsRegistration";
import ConsultationBilling from "./pages/ConsultationBilling";
import DoctorSpecialization from "./master/DoctorSpecialization";
import DoctorSpecializationList from "./master/DoctorSpecializationList";
import DepartmentList from "./pages/DepartmentList";
import Prescription from "./pages/Prescription";
import MedicineMaster from "./pages/MedicineMaster";
import MedicineCategoryMaster from "./pages/MedicineCategoryMaster";
import SupplierMaster from "./pages/SupplierMaster";
import UnitMeasurementMaster from "./pages/UnitMeasurementMaster";
import PurchaseOrder from "./pages/PurchaseOrder";
import GRN from "./pages/GRN";
import Stock from "./pages/Stock";
import PharmacyBilling from "./pages/PharmacyBilling";
import AdminDashboard from "./pages/AdminDashboard";
import TypeMaster from "./master/TypeMaster";
import GenericMaster from "./master/GenericMaster";
import CompanyName from "./master/CompanyName";
import PurchaseOrderApproval from "./pages/PurchaseOrderApproval";
import UserManagement from "./pages/UserManagement";
import ActivityHistory from "./pages/ActivityHistory";
import AppointmentManagement from "./pages/AppointmentsManagement";
import AppointmentPatientsUpdate from "./pages/AppointmentPatientsUpdate";

const SIDEBAR_WIDTHS = {
  expanded: "12rem",
  collapsed: "2.5rem",
};

export default function App() {
  const { user, loading } = useContext(AuthContext);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTHS.collapsed : SIDEBAR_WIDTHS.expanded;

  const RequireAuth = ({ children }) => {
    if (loading) return null;
    const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!user?.token && !storedToken) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Toaster
        reverseOrder={false}
        position="top-center"
        toastOptions={{
          style: {
            fontWeight: 600,
            fontSize: "13px",
            fontFamily: "sans-serif",
            maxWidth: "340px",
            width: "100%",
            borderRadius: "4px",
            padding: "6px 12px",
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
          },
          success: {
            icon: (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#14532d" 
                strokeWidth="2.5" 
                className="w-4 h-4 flex-shrink-0"
                style={{ marginRight: "6px" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            style: {
              background: "#f0fdf4",
              color: "#14532d",
              border: "1px solid #15803d",
            },
          },
          error: {
            icon: (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#991b1b" 
                strokeWidth="2.5" 
                className="w-4 h-4 flex-shrink-0"
                style={{ marginRight: "6px" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            style: {
              background: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #b91c1c",
            },
          },
        }}
      />


      <Routes>
        {/* 1. Login Route: No Sidebar, Full Screen */}
        <Route path="/" element={<Login />} />

        {/* 2. All Protected Routes: With Sidebar Layout */}
        <Route
          path="*"
          element={
            <RequireAuth>
              <div
                className="flex h-screen overflow-hidden bg-slate-50"
                style={{ "--sidebar-width": sidebarWidth }}
              >
                <Sidebar
                  collapsed={sidebarCollapsed}
                  onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
                  width={sidebarWidth}
                />

                <main className="flex h-screen flex-1 flex-col overflow-hidden transition-all duration-300 lg:ml-[var(--sidebar-width)]">
                  <div className="flex-1 overflow-y-auto pt-16 pb-20 lg:pt-0 lg:pb-0">
                    <Routes>
                      <Route path="/dashboard" element={<AdminDashboard />} />
                
                      <Route path="/DoctorRegistration" element={<DoctorRegistration />} />
                      <Route path="/DoctoreSchedule" element={<DoctoreSchedule />} />
                      <Route path="/AppointmentBooking" element={<AppointmentBooking />} />
                      <Route path="/PatientsRegistration" element={<PatientsRegistration />} />
                      <Route path="/ConsultationBilling" element={<ConsultationBilling />} />
                      <Route path="/DoctorSpecialization" element={<DoctorSpecialization />} />
                      <Route path="/DoctorSpecializationList" element={<DoctorSpecializationList />} />
                      <Route path="/DepartmentList" element={<DepartmentList/>} />
                      <Route path="/Prescription" element={<Prescription/>} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/MedicineMaster" element={<MedicineMaster/>} />
                      <Route path="/MedicineCategoryMaster" element={<MedicineCategoryMaster/>} />
                      <Route path="/SupplierMaster" element={<SupplierMaster/>} />
                      <Route path="/UnitMeasurementMaster" element={<UnitMeasurementMaster/>} />
                      <Route path="/PurchaseOrder" element={<PurchaseOrder/>} />
                      <Route path="/GRN" element={<GRN/>} />
                      <Route path="/Stock" element={<Stock/>} />
                      <Route path="/PharmacyBilling" element={<PharmacyBilling/>} />
                      <Route path="/TypeMaster" element={<TypeMaster/>} />
                      <Route path="/GenericMaster" element={<GenericMaster/>} />
                      <Route path="/CompanyName" element={<CompanyName/>} />
                      <Route path="/PurchaseOrderApproval" element={<PurchaseOrderApproval/>} />
                      <Route path="/UserManagement" element={<UserManagement/>} />
                      <Route path="/ActivityHistory" element={<ActivityHistory/>} />
                      <Route path="/AppointmentManagement" element={<AppointmentManagement/>} />
                      <Route path="/AppointmentPatientsUpdate" element={<AppointmentPatientsUpdate/>} />

                    </Routes>
                  </div>
                </main>
              </div>
            </RequireAuth>
          }
        />
      </Routes>
    </div>
  );
}
